import { useState, useEffect } from "react";
import { FaCheck, FaRegEdit } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { Link } from "react-router-dom";
import DynamicSelect from "../../DynamicSelect";
import TableSkeleton from "../../TableSkeleton";
import Pagination from "../../Shared/Pagination";

const PatientTable = ({
  data,
  searchTerm,
  itemsPerPage = 10,
  handleSearch,
  handleEdit,
  setData,
  resetFlag,
  openDeleteModal,
  isLoading = false
}) => {
  const [selectedRows, setSelectedRows] = useState(
    new Array(data.length).fill(false)
  );
  const [isHeaderChecked, setIsHeaderChecked] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [genderFilter, setGenderFilter] = useState(null);
  const [bloodGroupFilter, setBloodGroupFilter] = useState(null);
  const [genderOptions, setGenderOptions] = useState([]);
  const [bloodGroupOptions, setBloodGroupOptions] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);


  const skeletonColumns = [
    { width: 16, height: 16, className: "p-3", skeletonClassName: "ml-3" }, // Checkbox
    { width: 40, height: 14, className: "p-3 pl-5" }, // SL No
    { width: 80, height: 14, className: "p-3" }, // ID
    { width: 200, height: 14, className: "p-3" }, // Name (including image placeholder)
    { width: 80, height: 14, className: "p-3" }, // Gender
    { width: 80, height: 14, className: "p-3" }, // Blood Group
    { width: 120, height: 14, className: "p-3" }, // Phone Number
    { width: 100, height: 28, className: "p-3 text-center", skeletonClassName: "inline-block" }, // Actions
  ];

  // Update the header checkbox state based on individual checkboxes
  useEffect(() => {
    setIsHeaderChecked(selectedRows.every((isSelected) => isSelected));
  }, [selectedRows]);

  useEffect(() => {
    setSelectedRows(new Array(data.length).fill(false));
  }, [resetFlag, data.length]);

  // Update the gender and blood group dropdown options based on the patient data
  useEffect(() => {
    if (data.length > 0) {
      const uniqueGenders = Array.from(
        new Set(
          data
            .map((patient) => patient.gender)
            .filter((g) => g !== null && g !== undefined)
        )
      );
      const uniqueBloodGroups = Array.from(
        new Set(
          data
            .map((patient) => patient.bloodGroup)
            .filter((b) => b !== null && b !== undefined)
        )
      );

      // Set gender options
      setGenderOptions([
        { value: "all", label: "All" },
        ...uniqueGenders.map((gender) => ({
          value: gender,
          label: gender
            ? gender.charAt(0).toUpperCase() + gender.slice(1)
            : "Unknown",
        })),
      ]);

      // Set blood group options
      setBloodGroupOptions([
        { value: "all", label: "All" },
        ...uniqueBloodGroups.map((bloodGroup) => ({
          value: bloodGroup,
          label: bloodGroup ? bloodGroup : "Unknown",
        })),
      ]);
    }
  }, [data]);

   useEffect(() => {
        setTotalRecords(data.length);
      }, [data.length]);

  // Filter patients based on gender, blood group, and search term
  const filterPatients = () => {
    let filtered = data;

    // Filter by gender
    if (genderFilter && genderFilter !== "all") {
      filtered = filtered.filter((patient) => patient.gender === genderFilter);
    }

    // Filter by blood group
    if (bloodGroupFilter && bloodGroupFilter !== "all") {
      filtered = filtered.filter(
        (patient) => patient.bloodGroup === bloodGroupFilter
      );
    }

    // Apply the search term filter
    if (searchTerm) {
      filtered = filtered.filter((patient) => {
        const id = patient.id?.toLowerCase() || "";
        const name = patient.name?.toLowerCase() || "";
        const phoneNumber = patient.phoneNumber?.toLowerCase() || "";
        return (
          id.includes(searchTerm) ||
          name.includes(searchTerm) ||
          phoneNumber.includes(searchTerm)
        );
      });
    }

    return filtered;
  };

  const handleHeaderCheckboxChange = () => {
    const newSelectedRows = selectedRows.map(() => !isHeaderChecked);
    setSelectedRows(newSelectedRows);
    setIsHeaderChecked(!isHeaderChecked); // Toggle header checkbox state
  };

  const handleRowCheckboxChange = (index) => {
    const newSelectedRows = [...selectedRows];
    newSelectedRows[index] = !newSelectedRows[index];
    setSelectedRows(newSelectedRows);
  };

  // Calculate the total number of pages
  const totalPages = Math.ceil(filterPatients().length / itemsPerPage);

  // Handle page change
  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Slice the data based on the current page and itemsPerPage (10)
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filterPatients().slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div>
      <div className="bg-white shadow-sm rounded-lg">
        {/* Table Top */}
        <div className="flex flex-col lg:flex-row md:items-start lg:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 gap-3">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Patient List
            </h2>
            {selectedRows.some((isSelected) => isSelected) && (
              <button
                onClick={() => {
                  const selectedPatientIds = data
                    .map((patient, index) =>
                      selectedRows[index] ? patient.id : null
                    )
                    .filter((id) => id !== null);
                  openDeleteModal(selectedPatientIds);
                }}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />{" "}
              </button>
            )}
          </div>
          <div className="inline-flex flex-wrap flex-col sm:flex-row sm:items-center justify-start sm:justify-end gap-3">
            <div className="w-full sm:w-48 h-10 shrink-0 capitalize">
              {/* Gender Filter */}
              <DynamicSelect
                options={genderOptions}
                onChange={(selectedOption) =>
                  setGenderFilter(selectedOption?.value || null)
                }
                value={
                  genderFilter
                    ? {
                        value: genderFilter,
                        label:
                          genderFilter.charAt(0).toUpperCase() +
                          genderFilter.slice(1),
                      }
                    : null
                }
                placeholder="Select Gender"
                isSearchable
              />
            </div>
            <div className="w-full sm:w-48 h-10 shrink-0 capitalize">
              {/* Blood Group Filter */}
              <DynamicSelect
                options={bloodGroupOptions}
                onChange={(selectedOption) =>
                  setBloodGroupFilter(selectedOption?.value || null)
                }
                value={
                  bloodGroupFilter
                    ? { value: bloodGroupFilter, label: bloodGroupFilter }
                    : null
                }
                placeholder="Select Blood Group"
                isSearchable
              />
            </div>
            <input
              type="text"
              className="sm:max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by Name, ID, or Phone"
              value={searchTerm}
              onChange={handleSearch}
            />
            <Link
              to="/add-patient"
              className="bg-[#73CB64] text-center hover:bg-M-primary-color default-button shrink-0"
            >
              + Add Patient
            </Link>
          </div>
        </div>

        {/* Table Area */}
        <div className="overflow-x-auto">
          <table className="min-w-[1000px] table-auto w-full divide-y divide-gray-200">
            <thead>
              <tr className="px-3 bg-[#FBFBFD]">
                <th className="text-left">
                  <div className="relative left-3 top-[0px]">
                    <input
                      type="checkbox"
                      id="agreement"
                      className="hidden peer"
                      checked={isHeaderChecked}
                      onChange={handleHeaderCheckboxChange}
                    />
                    <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[4px] left-0 z-0">
                      <FaCheck className="text-slate-100" />
                    </span>
                    <label
                      htmlFor="agreement"
                      className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                    ></label>
                  </div>
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  SL NO
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  ID
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Name
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Gender
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Blood Group
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Phone Number
                </th>
                <th className="text-center uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <TableSkeleton columns={skeletonColumns} rowCount={5} />
              ) : currentData.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-4 text-center">
                    No records found.
                  </td>
                </tr>
              ) : (
               currentData.map((patient, index) => {
                const serialNumber =
                  (currentPage - 1) * itemsPerPage + index + 1;
                return (
                  <tr
                    key={patient.id}
                    className="border-b border-M-text-color/10 hover:bg-gray-100 border-collapse"
                  >
                    <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color">
                      <div className="relative left-3 -top-[3px]">
                        <input
                          type="checkbox"
                          id={"patient" + index}
                          className="hidden peer"
                          checked={selectedRows[index]}
                          onChange={() => handleRowCheckboxChange(index)}
                        />
                        <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[5px] left-0 z-0">
                          <FaCheck className="text-slate-100" />
                        </span>
                        <label
                          htmlFor={"patient" + index}
                          className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                        ></label>
                      </div>
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3 pl-5">
                      {serialNumber}
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                      {patient.id}
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                      <div className="flex items-center gap-2">
                        <img
                          src={`https://api.muktihospital.com${patient.image}`}
                          alt={patient.name || "Patient"}
                          onError={(e) =>
                            (e.target.src = "https://placehold.co/100")
                          }
                          className="size-8 rounded-full border border-M-text-color/20 shrink-0"
                        />
                        {patient.name}
                      </div>
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-xs py-5 px-3">
                      <span
                        className={`px-2 py-1 rounded-md ${
                          patient.gender && typeof patient.gender === "string"
                            ? patient.gender.toLowerCase() === "male"
                              ? "bg-[#783BFF] text-white"
                              : patient.gender.toLowerCase() === "female"
                              ? "bg-[#F9C45C] text-white"
                              : "bg-gray-200 text-M-heading-color"
                            : "bg-gray-200 text-M-heading-color"
                        }`}
                      >
                        {patient.gender || "Unknown"}
                      </span>
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                      {patient.bloodGroup}
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                      {patient.phoneNumber}
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEdit(patient.id)}
                          className="size-7 rounded-full bg-M-Green-color/10 text-M-Green-color text-sm inline-flex items-center justify-center hover:bg-M-Green-color hover:text-white transition-all duration-300"
                          title="Edit"
                        >
                          <FaRegEdit />
                        </button>
                        <button
                          onClick={() => openDeleteModal([patient.id])}
                          className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                          title="Delete"
                        >
                          <RiDeleteBin6Line />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
             })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalRecords={filterPatients().length}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default PatientTable;
